import DOMPurify from 'dompurify'

const YOUTUBE_HOSTS = new Set([
  'youtube.com',
  'www.youtube.com',
  'm.youtube.com',
  'youtu.be',
  'youtube-nocookie.com',
  'www.youtube-nocookie.com'
])

function toUrl(src) {
  try {
    return new URL(src)
  } catch {
    return null
  }
}

function getYouTubeVideoId(url) {
  const pathParts = url.pathname.split('/').filter(Boolean)

  if (url.hostname === 'youtu.be') return pathParts[0] ?? null
  if (pathParts[0] === 'embed' || pathParts[0] === 'shorts') return pathParts[1] ?? null
  if (pathParts[0] === 'watch') return url.searchParams.get('v')

  return null
}

function getVimeoVideoId(url) {
  const pathParts = url.pathname.split('/').filter(Boolean)
  return pathParts[pathParts.length - 1] ?? null
}

// YouTube/Vimeo iframes are replaced with a thumbnail link that opens the
// video on its own site in a new tab — this sidesteps embedded-player issues
// (third-party cookie blocking, anti-bot checks) and is trivially responsive
// since it's just an <img>, not an iframe with a fixed pixel height.
function getVideoLinkInfo(src) {
  const url = toUrl(src)
  if (!url) return null

  if (YOUTUBE_HOSTS.has(url.hostname)) {
    const videoId = getYouTubeVideoId(url)
    if (!videoId) return null
    return {
      watchUrl: `https://www.youtube.com/watch?v=${encodeURIComponent(videoId)}`,
      thumbnailUrl: `https://img.youtube.com/vi/${encodeURIComponent(videoId)}/hqdefault.jpg`
    }
  }

  if (url.hostname === 'player.vimeo.com') {
    const videoId = getVimeoVideoId(url)
    if (!videoId) return null
    return {
      watchUrl: `https://vimeo.com/${encodeURIComponent(videoId)}`,
      thumbnailUrl: null
    }
  }

  return null
}

function buildVideoLinkNode(doc, { watchUrl, thumbnailUrl }) {
  const link = doc.createElement('a')
  link.setAttribute('href', watchUrl)
  link.setAttribute('target', '_blank')
  link.setAttribute('rel', 'noopener noreferrer')
  link.setAttribute('class', 'embedded-video-link')
  link.setAttribute('aria-label', 'Play video on YouTube')

  if (thumbnailUrl) {
    const img = doc.createElement('img')
    img.setAttribute('src', thumbnailUrl)
    img.setAttribute('alt', '')
    img.setAttribute('loading', 'lazy')
    img.setAttribute('draggable', 'false')
    img.setAttribute('class', 'embedded-video-link__thumbnail')
    link.appendChild(img)
  }

  const play = doc.createElement('span')
  play.setAttribute('class', 'embedded-video-link__play')
  play.setAttribute('aria-hidden', 'true')
  link.appendChild(play)

  return link
}

function replaceVideoIframe(node) {
  const linkInfo = getVideoLinkInfo(node.getAttribute('src') || '')
  if (!linkInfo) {
    node.remove()
    return
  }

  node.replaceWith(buildVideoLinkNode(node.ownerDocument, linkInfo))
}

export function normalizeEmbeddedContent(html) {
  const parser = new DOMParser()
  const doc = parser.parseFromString(html, 'text/html')

  doc.querySelectorAll('iframe').forEach(replaceVideoIframe)

  return doc.body.innerHTML
}

DOMPurify.addHook('afterSanitizeAttributes', (node) => {
  if (node.tagName === 'A' && node.getAttribute('target') === '_blank') {
    node.setAttribute('rel', 'noopener noreferrer')
  }

  if (node.tagName === 'IFRAME') {
    replaceVideoIframe(node)
  }
})

class HtmlSanitizerService {
  sanitize(html, options = {}) {
    return DOMPurify.sanitize(html, {
      ADD_TAGS: ['iframe'],
      ADD_ATTR: ['target', 'style'],
      ...options
    })
  }
}

export default new HtmlSanitizerService()
